import { useState, useCallback, useMemo } from 'react';
import { Match } from 'effect';
import { partition } from 'lodash';
import { bulkSetProxy, removeFundProxy } from 'venn-api';
import { Notifications, NotificationType } from 'venn-ui-kit';
import {
  analyticsService,
  invalidate,
  getProxyTypeLabel,
  createFirstLayerProxyTypeValue,
  getExtrapolateFromValue,
} from 'venn-utils';
import type { FundToBulkProxy, FundToProxy, ProxyMetaWithName, InvestmentInfo, LayerProxyInfo } from '../../types';
import {
  ProxyPickerActionsDependencies,
  type ProxyPickerActionsDependencies as ProxyPickerActionsDependenciesType,
  type ProxyPickerAction as ProxyPickerActionType,
} from './types';
import { buildProxyMeta } from '../utils';

interface ExecuteBulkSaveParams {
  proxyMetas: ProxyMetaWithName[];
  investmentsWithErrors: (FundToProxy | FundToBulkProxy)[];
  investmentsWithoutErrors: (FundToProxy | FundToBulkProxy)[];
  onProxyChange: ProxyPickerActionsDependencies['mutations']['onProxyChange'];
  onClose: () => void;
  setIsUpdating: (isUpdating: boolean) => void;
  onSuccess: () => void;
  onError: () => void;
}

const executeBulkSave = async ({
  proxyMetas,
  investmentsWithErrors,
  investmentsWithoutErrors,
  onProxyChange,
  onClose,
  setIsUpdating,
  onSuccess,
  onError,
}: ExecuteBulkSaveParams): Promise<void> => {
  setIsUpdating(true);

  try {
    const results = await bulkSetProxy(proxyMetas);
    await invalidate(
      'investments',
      investmentsWithoutErrors.map(({ id }) => id),
    );
    onProxyChange?.(
      results.content.map((proxyMeta) => {
        const matchingMeta = proxyMetas.find(
          (pm) => pm.fundId === proxyMeta.fundId && pm.proxyOrder === proxyMeta.proxyOrder,
        );
        return { ...proxyMeta, proxyName: matchingMeta?.proxyName ?? '' };
      }),
      investmentsWithErrors,
    );
    onSuccess();
  } catch {
    onError();
  } finally {
    setIsUpdating(false);
    onClose();
  }
};

const partitionInvestments = (investments: (FundToProxy | FundToBulkProxy)[], investmentInfo: InvestmentInfo) =>
  partition(investments, (investment) => investmentInfo[investment.id]?.disableSave);

type LegacyDeps = Extract<ProxyPickerActionsDependenciesType, { _tag: 'Legacy' }>;

const saveLegacyProxy = async ({
  state: { proxy: proxyState, investments, proxySetId, validationState, hasSingleProxy },
  mutations: { onProxyChange, onClose, setIsUpdating },
}: LegacyDeps): Promise<void> => {
  const { selectedProxyType, selectedProxy, shouldExtrapolate, numLags } = proxyState;

  if (validationState.state !== 'ready' || !selectedProxyType || !selectedProxy) {
    return;
  }

  const { investmentInfo } = validationState;
  const [investmentsWithErrors, investmentsWithoutErrors] = partitionInvestments(investments, investmentInfo);

  // Create the proxy info object with the correct discriminated union type
  const createProxyInfo = (): LayerProxyInfo => {
    const baseInfo = { selectedProxy, numLags };

    if (selectedProxyType === 'SUBSTITUTE' || selectedProxyType === 'EXTRAPOLATE') {
      return { ...baseInfo, proxyType: selectedProxyType };
    }

    if (selectedProxyType === 'BACKFILL') {
      return shouldExtrapolate
        ? { ...baseInfo, proxyType: 'BACKFILL', extrapolate: true as const }
        : { ...baseInfo, proxyType: 'BACKFILL', extrapolate: false as const };
    }

    // For INTERPOLATE, DESMOOTH, DESMOOTH_INTERPOLATE
    return { ...baseInfo, proxyType: selectedProxyType, extrapolate: shouldExtrapolate };
  };

  const proxyMetas = investmentsWithoutErrors
    .map((investment): ProxyMetaWithName | null =>
      buildProxyMeta({
        investmentId: investment.id,
        proxyInfo: createProxyInfo(),
        proxyOrder: 0,
        hasSingleProxy,
        investmentInfo,
        proxySetId: proxySetId ?? null,
      }),
    )
    .filter((m): m is ProxyMetaWithName => m !== null);

  await executeBulkSave({
    proxyMetas,
    investmentsWithErrors,
    investmentsWithoutErrors,
    onProxyChange,
    onClose,
    setIsUpdating,
    onSuccess: () => {
      analyticsService.bulkProxyApplied({
        numSelected: investments.length,
        numApplied: investmentsWithoutErrors.length,
        numIneligible: investmentsWithErrors.length,
        extrapolate: shouldExtrapolate,
        proxyType: selectedProxyType,
      });
      Notifications.notify(
        `${investmentsWithoutErrors.length} proxies updated successfully.`,
        NotificationType.SUCCESS,
      );
    },
    onError: () => {
      onProxyChange?.([], investments);
      Notifications.notify('Failed to update proxies.', NotificationType.ERROR);
    },
  });
};

type LayeredDeps = Extract<ProxyPickerActionsDependenciesType, { _tag: 'Layered' }>;

const saveLayeredProxy = async ({
  state: { proxy: proxyState, investments, proxySetId, validationState, hasSingleProxy },
  mutations: { onProxyChange, onClose, setIsUpdating },
}: LayeredDeps): Promise<void> => {
  const { configuration } = proxyState;

  if (validationState.state !== 'ready') {
    return;
  }

  const { investmentInfo } = validationState;
  const [investmentsWithErrors, investmentsWithoutErrors] = partitionInvestments(investments, investmentInfo);

  const { proxyMetas, analyticsLabel, hasAnyExtrapolation } = Match.value(configuration).pipe(
    Match.tag('Empty', () => ({
      proxyMetas: [] as ProxyMetaWithName[],
      analyticsLabel: '',
      hasAnyExtrapolation: false,
    })),
    Match.tag('SingleLayerable', ({ proxy }) => {
      if (!proxy.selectedProxy) {
        return { proxyMetas: [] as ProxyMetaWithName[], analyticsLabel: '', hasAnyExtrapolation: false };
      }

      const metas = investmentsWithoutErrors
        .map((investment) =>
          buildProxyMeta({
            investmentId: investment.id,
            proxyInfo: proxy,
            proxyOrder: 0,
            hasSingleProxy,
            investmentInfo,
            proxySetId,
          }),
        )
        .filter((m): m is ProxyMetaWithName => m !== null);

      return {
        proxyMetas: metas,
        analyticsLabel: getProxyTypeLabel(
          createFirstLayerProxyTypeValue(proxy.proxyType, getExtrapolateFromValue(proxy)),
        ),
        hasAnyExtrapolation: getExtrapolateFromValue(proxy),
      };
    }),
    Match.tag('SingleNonLayerable', ({ proxy }) => {
      if (!proxy.selectedProxy) {
        return { proxyMetas: [] as ProxyMetaWithName[], analyticsLabel: '', hasAnyExtrapolation: false };
      }

      const metas = investmentsWithoutErrors
        .map((investment) =>
          buildProxyMeta({
            investmentId: investment.id,
            proxyInfo: proxy,
            proxyOrder: 0,
            hasSingleProxy,
            investmentInfo,
            proxySetId,
          }),
        )
        .filter((m): m is ProxyMetaWithName => m !== null);

      return {
        proxyMetas: metas,
        analyticsLabel: getProxyTypeLabel(
          createFirstLayerProxyTypeValue(proxy.proxyType, getExtrapolateFromValue(proxy)),
        ),
        hasAnyExtrapolation: getExtrapolateFromValue(proxy),
      };
    }),
    Match.tag('Multi', ({ firstLayer, secondLayer }) => {
      if (!firstLayer.selectedProxy || !secondLayer.selectedProxy) {
        return { proxyMetas: [] as ProxyMetaWithName[], analyticsLabel: '', hasAnyExtrapolation: false };
      }

      const metas = investmentsWithoutErrors.flatMap((investment) => {
        const first = buildProxyMeta({
          investmentId: investment.id,
          proxyInfo: firstLayer,
          proxyOrder: 0,
          hasSingleProxy,
          investmentInfo,
          proxySetId,
        });

        const second = buildProxyMeta({
          investmentId: investment.id,
          proxyInfo: secondLayer,
          proxyOrder: 1,
          hasSingleProxy,
          investmentInfo,
          proxySetId,
        });

        return [first, second].filter((m): m is ProxyMetaWithName => m !== null);
      });

      const firstLabel = getProxyTypeLabel(
        createFirstLayerProxyTypeValue(firstLayer.proxyType, getExtrapolateFromValue(firstLayer)),
      );

      const secondLabel = getProxyTypeLabel(
        createFirstLayerProxyTypeValue(secondLayer.proxyType, getExtrapolateFromValue(secondLayer)),
      );

      return {
        proxyMetas: metas,
        analyticsLabel: `${firstLabel} + ${secondLabel}`,
        hasAnyExtrapolation: getExtrapolateFromValue(firstLayer) || getExtrapolateFromValue(secondLayer),
      };
    }),
    Match.exhaustive,
  );

  if (proxyMetas.length === 0) {
    return;
  }

  await executeBulkSave({
    proxyMetas,
    investmentsWithErrors,
    investmentsWithoutErrors,
    onProxyChange,
    onClose,
    setIsUpdating,
    onSuccess: () => {
      analyticsService.bulkProxyApplied({
        numSelected: investments.length,
        numApplied: investmentsWithoutErrors.length,
        numIneligible: investmentsWithErrors.length,
        extrapolate: hasAnyExtrapolation,
        proxyType: analyticsLabel,
      });
      Notifications.notify(
        `${investmentsWithoutErrors.length} proxies updated successfully.`,
        NotificationType.SUCCESS,
      );
    },
    onError: () => {
      onProxyChange?.([], investments);
      Notifications.notify('Failed to update proxies.', NotificationType.ERROR);
    },
  });
};

const removeProxy = async (deps: ProxyPickerActionsDependenciesType): Promise<void> => {
  const {
    state: { investments, proxySetId },
    mutations: { onProxyChange, onClose, setIsUpdating },
  } = deps;
  const investment = investments[0];
  if (!investment) {
    return;
  }

  setIsUpdating(true);
  try {
    await removeFundProxy(investment.id, proxySetId);
    Notifications.notify(
      deps.state.isResetOperation ? 'Proxy successfully reset.' : 'Proxy settings successfully removed.',
      NotificationType.SUCCESS,
    );
    invalidate('investment', investment.id);
    onProxyChange?.([], []);
  } catch {
    onProxyChange?.([], [investment]);
    Notifications.notify('Failed to remove proxy.', NotificationType.ERROR);
  } finally {
    onClose();
    setIsUpdating(false);
  }
};

export const useProxyPickerActions = (deps: ProxyPickerActionsDependenciesType) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const depsWithUpdating = useMemo(
    (): ProxyPickerActionsDependenciesType =>
      Match.value(deps).pipe(
        Match.tag('Legacy', (d) =>
          ProxyPickerActionsDependencies.Legacy({
            ...d,
            mutations: { ...d.mutations, setIsUpdating },
          }),
        ),
        Match.tag('Layered', (d) =>
          ProxyPickerActionsDependencies.Layered({
            ...d,
            mutations: { ...d.mutations, setIsUpdating },
          }),
        ),
        Match.exhaustive,
      ),
    [deps, setIsUpdating],
  );

  const performAction = useCallback(
    (action: ProxyPickerActionType) =>
      Match.value(action).pipe(
        Match.tag('Save', () =>
          Match.value(depsWithUpdating).pipe(
            Match.tag('Legacy', saveLegacyProxy),
            Match.tag('Layered', saveLayeredProxy),
            Match.exhaustive,
          ),
        ),
        Match.tag('Remove', () => removeProxy(depsWithUpdating)),
        Match.tag('SelectProxyType', ({ value }) => {
          const extrapolate = getExtrapolateFromValue(value);
          Match.value(deps).pipe(
            Match.tag(
              'Legacy',
              ({
                mutations: {
                  proxy: { setProxyType, setExtrapolate },
                },
              }) => {
                setProxyType(value.proxyType);
                setExtrapolate(extrapolate);
              },
            ),
            Match.tag(
              'Layered',
              ({
                mutations: {
                  proxy: { setFirstLayerProxyType, setFirstLayerExtrapolate },
                },
                state: { proxy },
              }) => {
                const { proxyType: currentProxyType, extrapolate: currentExtrapolate } = Match.value(
                  proxy.configuration,
                ).pipe(
                  Match.tag('SingleLayerable', ({ proxy: config }) => ({
                    proxyType: config.proxyType,
                    extrapolate: getExtrapolateFromValue(config),
                  })),
                  Match.tag('SingleNonLayerable', ({ proxy: config }) => ({
                    proxyType: config.proxyType,
                    extrapolate: getExtrapolateFromValue(config),
                  })),
                  Match.tag('Multi', ({ firstLayer }) => ({
                    proxyType: firstLayer.proxyType,
                    extrapolate: getExtrapolateFromValue(firstLayer),
                  })),
                  Match.tag('Empty', () => ({ proxyType: undefined, extrapolate: undefined })),
                  Match.exhaustive,
                );

                if (currentProxyType !== value.proxyType || currentExtrapolate !== extrapolate) {
                  setFirstLayerProxyType(value.proxyType);
                  setFirstLayerExtrapolate(extrapolate);
                }
              },
            ),
            Match.exhaustive,
          );
        }),
        Match.tag('SelectProxy', ({ proxy }) => {
          Match.value(deps).pipe(
            Match.tag(
              'Legacy',
              ({
                mutations: {
                  proxy: { setProxy },
                },
              }) => setProxy(proxy),
            ),
            Match.tag(
              'Layered',
              ({
                state: {
                  proxy: { activeTab },
                },
                mutations: {
                  proxy: { setFirstLayerProxy, setSecondLayerProxy },
                },
              }) => (activeTab === 'second' ? setSecondLayerProxy(proxy) : setFirstLayerProxy(proxy)),
            ),
            Match.exhaustive,
          );
        }),
        Match.tag('SetNumLags', ({ lags }) => {
          Match.value(deps).pipe(
            Match.tag(
              'Legacy',
              ({
                mutations: {
                  proxy: { setNumLags },
                },
              }) => setNumLags(lags),
            ),
            Match.tag(
              'Layered',
              ({
                state: {
                  proxy: { activeTab },
                },
                mutations: {
                  proxy: { setFirstLayerNumLags, setSecondLayerNumLags },
                },
              }) => (activeTab === 'second' ? setSecondLayerNumLags(lags) : setFirstLayerNumLags(lags)),
            ),
            Match.exhaustive,
          );
        }),
        Match.tag('SetExtrapolate', ({ extrapolate }) => {
          Match.value(deps).pipe(
            Match.tag(
              'Legacy',
              ({
                mutations: {
                  proxy: { setExtrapolate: set },
                },
              }) => set(extrapolate),
            ),
            Match.tag(
              'Layered',
              ({
                state: {
                  proxy: { activeTab },
                },
                mutations: {
                  proxy: { setFirstLayerExtrapolate, setSecondLayerExtrapolate },
                },
              }) =>
                activeTab === 'second' ? setSecondLayerExtrapolate(extrapolate) : setFirstLayerExtrapolate(extrapolate),
            ),
            Match.exhaustive,
          );
        }),
        Match.tag('SelectSecondLayerProxyType', ({ value }) => {
          Match.value(deps).pipe(
            Match.tag('Legacy', () => {}),
            Match.tag(
              'Layered',
              ({
                mutations: {
                  proxy: { setSecondLayerProxyType },
                },
              }) => setSecondLayerProxyType(value.proxyType, getExtrapolateFromValue(value)),
            ),
            Match.exhaustive,
          );
        }),
        Match.tag('RemoveSecondLayer', () => {
          Match.value(deps).pipe(
            Match.tag('Legacy', () => {}),
            Match.tag(
              'Layered',
              ({
                mutations: {
                  proxy: { removeSecondLayer },
                },
              }) => removeSecondLayer(),
            ),
            Match.exhaustive,
          );
        }),
        Match.tag('ChangeTab', ({ tab }) => {
          Match.value(deps).pipe(
            Match.tag('Legacy', () => {}),
            Match.tag(
              'Layered',
              ({
                mutations: {
                  proxy: { setActiveTab },
                },
              }) => setActiveTab(tab),
            ),
            Match.exhaustive,
          );
        }),
        Match.exhaustive,
      ),
    [deps, depsWithUpdating],
  );

  return {
    performAction,
    isUpdating,
  };
};
